/**
 * React Query hooks for admin user management
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminUsersApi } from '../api/admin-users';
import type { AdminUserUpdate } from '../types/admin-users';

const QUERY_KEY = 'admin-users';

/**
 * Fetch all users, optionally filtered by tenant
 */
export function useAdminUsers(tenantId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, tenantId],
    queryFn: () => adminUsersApi.list(tenantId),
  });
}

/**
 * Fetch a single user by ID
 */
export function useAdminUser(userId: string) {
  return useQuery({
    queryKey: [QUERY_KEY, userId],
    queryFn: () => adminUsersApi.get(userId),
    enabled: !!userId,
  });
}

/**
 * Update user enabled status
 */
export function useUpdateAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: AdminUserUpdate }) =>
      adminUsersApi.update(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

/**
 * Trigger password reset for a user
 */
export function useResetPassword() {
  return useMutation({
    mutationFn: (userId: string) => adminUsersApi.resetPassword(userId),
  });
}
